<template>
    <div>
        <!-- Deferred / pro-forma invoice modal -->
        <CustomDialog
            :value="showDeferredModal"
            :default-title="$t('invoices.deferred_modal.title')"
            @cancel="closeDeferredModal"
        >
            <template #content>
                <div
                    class="ppv2-bg-gradient-to-r ppv2-from-[#8241D7] ppv2-to-[#3A329D] ppv2-px-5 ppv2-py-4 ppv2-rounded-[10px] ppv2-flex ppv2-justify-between ppv2-items-center ppv2-mb-6 ppv2-text-white"
                >
                    <div class="ppv2-flex ppv2-flex-col">
                        <span class="poppinsMedium" style="font-size: 20px">{{ $t('invoices.deferred_modal.order') }}</span>
                        <span class="poppinsSemiBold" style="font-size: 20px">#{{ displayOrderId }}</span>
                    </div>
                    <div class="ppv2-flex ppv2-flex-col ppv2-items-end">
                        <span class="poppinsMedium" style="font-size: 20px">{{
                            $t('invoices.deferred_modal.total_amount')
                        }}</span>
                        <span class="poppinsSemiBold" style="font-size: 20px">{{ formatCurrency(displayOrderTotal) }}</span>
                    </div>
                </div>

                <InfoBanner class="ppv2-mb-6" variant="info">
                    {{ $t('invoices.deferred_modal.info_banner_1') }}<br />
                    <span class="ppv2-italic">{{ $t('invoices.deferred_modal.info_banner_2') }}</span>
                </InfoBanner>

                <div class="ppv2-mb-2">
                    <label class="ppv2-block ppv2-text-sm poppinsMedium ppv2-text-black ppv2-mb-1">
                        {{ $t('invoices.deferred_modal.due_date') }} <span class="ppv2-text-red-500">*</span>
                    </label>
                    <BaseDateInput
                        v-model="deferredDueDate"
                        :min-date="minDueDate"
                        :default-value="minDueDate"
                        :placeholder="$i18n.locale === 'fr' ? 'JJ/MM/AAAA' : 'MM/DD/YYYY'"
                        popup-class="base-date-input-popup"
                        input-class="ppv2-border-gray-200 dark:ppv2-border-gray-700 ppv2-rounded-lg"
                    />
                    <p v-if="deferredError" class="ppv2-text-red-500 ppv2-text-xs ppv2-mt-1">{{ deferredError }}</p>
                </div>
            </template>

            <template #actions>
                <div class="ppv2-flex ppv2-gap-4 ppv2-justify-around ppv2-w-full ppv2-px-6">
                    <BaseButton
                        variant="outline"
                        custom-class="ppv2-flex-1 !ppv2-rounded-xl ppv2-max-w-max"
                        :custom-height="48"
                        @click="closeDeferredModal"
                    >
                        {{ $t('common.cancel') }}
                    </BaseButton>
                    <BaseButton
                        variant="primary"
                        custom-class="ppv2-flex-1 !ppv2-rounded-xl ppv2-max-w-max"
                        :custom-height="48"
                        :loading="deferredLoading"
                        :disabled="!deferredDueDate"
                        @click="confirmDeferredInvoice"
                    >
                        {{ $t('invoices.deferred_modal.generate_invoice') }}
                    </BaseButton>
                </div>
            </template>
        </CustomDialog>

        <!-- Cancel order confirmation dialog -->
        <RefundFlowDialog
            :value="showCancelDialog"
            :title="$t('invoices.cancel_order')"
            :message="cancelDialogMessage"
            :confirm-text="$t('invoices.yes_cancel')"
            :cancel-text="$t('common.no')"
            variant="danger"
            @confirm="confirmCancelOrder"
            @cancel="showCancelDialog = false"
        />

        <!-- Result feedback dialog -->
        <RefundFlowDialog
            :value="showResultDialog"
            :title="resultDialogSuccess ? $t('invoices.success') : $t('invoices.error')"
            :message="resultDialogMessage"
            :confirm-text="$t('invoices.ok')"
            :variant="resultDialogSuccess ? 'success' : 'danger'"
            :hide-cancel="true"
            @confirm="showResultDialog = false"
            @cancel="showResultDialog = false"
        />

        <!-- Credit-note modal (per-method refund allocation) for 409 requires_allocation -->
        <CreateCreditNoteModal
            v-if="creditNoteData"
            :value="showCreditNoteModal"
            :order-id="creditNoteOrderId"
            :data="creditNoteData"
            @cancel="showCreditNoteModal = false"
            @done="onCreditNoteDone"
        />
    </div>
</template>

<script>
import moment from 'moment'
import RefundFlowDialog from '../Invoices/RefundFlowDialog.vue'
import BaseDateInput from '../Shared/BaseDateInput.vue'
import CustomDialog from '../Shared/CustomDialog.vue'
import BaseButton from '../Shared/BaseButton.vue'
import InfoBanner from '../Shared/InfoBanner.vue'
import CreateCreditNoteModal from '../Invoices/CreateCreditNoteModal.vue'

export default {
    name: 'OrderActionsDialogs',
    components: {
        RefundFlowDialog,
        BaseDateInput,
        CustomDialog,
        BaseButton,
        InfoBanner,
        CreateCreditNoteModal,
    },
    props: {
        orderId: {
            type: [Number, String],
            default: null,
        },
        orderTotal: {
            type: Number,
            default: 0,
        },
    },
    data() {
        return {
            showDeferredModal: false,
            deferredTargetOrder: null,
            deferredDueDate: '',
            deferredOpenedAt: null,
            deferredLoading: false,
            deferredError: null,
            showCancelDialog: false,
            cancelTargetOrder: null,
            showResultDialog: false,
            resultDialogMessage: '',
            resultDialogSuccess: false,
            showCreditNoteModal: false,
            creditNoteData: null,
            creditNoteOrderId: null,
        }
    },
    computed: {
        // Échéance minimale = demain, le backend imposant `after:today`. La date du jour
        // doit être GRISÉE dans le calendrier, pas seulement rejetée en 422.
        // `toISOString()` est interdit ici : il convertit en UTC et rendait la date du jour
        // sélectionnable dès que le décalage la ramenait à la veille (UTC+2 entre 00 h et
        // 02 h, tenant DOM-TOM à UTC+4 en soirée) → exactement le « given data was invalid ».
        // `deferredOpenedAt` sert de dépendance réactive : sans elle le computed reste en
        // cache pour la vie du composant, et un BO ouvert avant minuit proposerait la veille.
        minDueDate() {
            void this.deferredOpenedAt
            return this.nextDayIsoDate()
        },
        displayOrderId() {
            return this.deferredTargetOrder ? this.deferredTargetOrder.id : this.orderId
        },
        displayOrderTotal() {
            if (this.deferredTargetOrder) {
                return (this.deferredTargetOrder.total_price || 0) - (this.deferredTargetOrder.remise_price || 0)
            }
            return this.orderTotal
        },
        cancelDialogMessage() {
            const targetOrderId = this.cancelTargetOrder ? this.cancelTargetOrder.id : this.displayOrderId
            return `${this.$t('invoices.confirm_cancel_order')} #${targetOrderId} ?`
        },
    },
    methods: {
        formatCurrency(amount) {
            const locale = this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-US'
            return new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: this.$codeCurrency,
            }).format(amount || 0)
        },
        nextDayIsoDate() {
            const tomorrow = new Date()
            tomorrow.setDate(tomorrow.getDate() + 1)
            const month = String(tomorrow.getMonth() + 1).padStart(2, '0')
            const day = String(tomorrow.getDate()).padStart(2, '0')
            return `${tomorrow.getFullYear()}-${month}-${day}`
        },
        openDeferred(order, suggestedDueDate) {
            this.deferredTargetOrder = order || null
            // Échéance pré-sélectionnée = J + le délai paramétré dans Établissement > Facturation.
            // Elle est calculée par le serveur (ValidatedOrderInvoiceService::suggestedDueDate),
            // sur la même horloge que la règle `after:today` ; l'horloge du navigateur ne sert
            // que de repli si la commande n'a pas de suggestion (listing) ni de prop relayée
            // par le menu (page détail).
            this.deferredDueDate = (order && order.suggested_due_date) || suggestedDueDate || this.nextDayIsoDate()
            this.deferredError = null
            // Réévalue minDueDate à chaque ouverture : la borne doit suivre le passage de minuit.
            this.deferredOpenedAt = Date.now()
            this.showDeferredModal = true
        },
        closeDeferredModal() {
            this.showDeferredModal = false
            this.deferredError = null
        },
        async confirmDeferredInvoice() {
            if (!this.deferredDueDate) {
                this.deferredError = this.$t('invoices.deferred_modal.due_date_required')
                return
            }
            // Filet de sécurité : le calendrier grise déjà la date du jour, mais la borne
            // peut avoir vieilli (modale laissée ouverte au passage de minuit). On affiche un
            // message métier plutôt que de laisser remonter le 422 générique de Laravel.
            if (this.deferredDueDate < this.nextDayIsoDate()) {
                this.deferredError = this.$t('invoices.deferred_modal.due_date_must_be_future')
                return
            }
            this.deferredLoading = true
            this.deferredError = null
            try {
                const response = await this.axios.post('/back-office/invoices-v3/deferred-invoice', {
                    orderId: this.displayOrderId,
                    dueDate: this.deferredDueDate,
                })
                if (response.data.success) {
                    this.showDeferredModal = false
                    this.resultDialogSuccess = true
                    const dueDateFormat = this.$i18n.locale === 'fr' ? 'DD/MM/YYYY' : 'MM/DD/YYYY'
                    const dueDateFormatted = response.data.data.due_date
                        ? moment(response.data.data.due_date).format(dueDateFormat)
                        : '-'
                    this.resultDialogMessage = this.$t('invoices.deferred_modal.success_message', {
                        fiscal_number: response.data.data.fiscal_number,
                        due_date: dueDateFormatted,
                    })
                    this.showResultDialog = true
                    this.$emit('action-completed')
                } else {
                    this.deferredError = response.data.message
                }
            } catch (error) {
                // Laravel 8 renvoie toujours « The given data was invalid. » comme `message`
                // d'un 422 : le message métier se trouve dans `errors.<champ>[0]`.
                const data = error.response?.data
                const fieldErrors = data?.errors ? [].concat(...Object.values(data.errors)) : []
                const message = fieldErrors[0] || data?.message || this.$t('invoices.deferred_modal.error_occured')
                this.$toast.error(message)
                this.deferredError = message
            } finally {
                this.deferredLoading = false
            }
        },

        requestCancel(order) {
            if (order && (order.payment_status?.key === 'cancelled' || order.payment_status === 'cancelled')) {
                return
            }
            this.cancelTargetOrder = order || null
            this.showCancelDialog = true
        },
        async confirmCancelOrder() {
            this.showCancelDialog = false
            const targetOrderId = this.cancelTargetOrder ? this.cancelTargetOrder.id : this.displayOrderId
            if (!targetOrderId) return

            try {
                const response = await this.axios.post('/back-office/invoices-v3/cancel-order', {
                    orderId: targetOrderId,
                })

                if (response.data.success) {
                    const amountRefunded = response.data.data?.amountRefunded
                    let message = this.$t('invoices.order_cancelled_success')
                    if (amountRefunded && amountRefunded > 0) {
                        message += `. ${this.$t('invoices.amount_refunded')}: ${this.formatCurrency(amountRefunded)}`
                    }
                    this.resultDialogSuccess = true
                    this.resultDialogMessage = message
                    this.showResultDialog = true
                    this.$emit('action-completed')
                } else {
                    this.resultDialogSuccess = false
                    this.resultDialogMessage = this.$t('invoices.order_cancel_error')
                    this.showResultDialog = true
                }
            } catch (error) {
                const response = error.response
                // HTTP 409 + requires_allocation => the refund cannot be fully auto-covered;
                // open the credit-note modal so the operator can allocate per payment method.
                if (
                    response &&
                    response.status === 409 &&
                    response.data &&
                    response.data.requires_allocation &&
                    response.data.data
                ) {
                    this.creditNoteOrderId = targetOrderId
                    this.creditNoteData = response.data.data
                    this.showCreditNoteModal = true
                    return
                }
                this.$toast.error(response?.data?.message)
                this.resultDialogSuccess = false
                this.resultDialogMessage = response?.data?.message || this.$t('invoices.order_cancel_error')
                this.showResultDialog = true
            } finally {
                this.cancelTargetOrder = null
            }
        },
        onCreditNoteDone(data) {
            this.showCreditNoteModal = false
            const amountRefunded = data && data.amountRefunded
            let message = this.$t('invoices.order_cancelled_success')
            if (amountRefunded && amountRefunded > 0) {
                message += `. ${this.$t('invoices.amount_refunded')}: ${this.formatCurrency(amountRefunded)}`
            }
            this.resultDialogSuccess = true
            this.resultDialogMessage = message
            this.showResultDialog = true
            this.creditNoteData = null
            this.creditNoteOrderId = null
            this.$emit('action-completed')
        },
    },
}
</script>
